import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { groupByShelf } from '../shelves.js';
import BookSheet from './BookSheet.jsx';
import { BOARD, CASE_H, CASE_W, FLOOR_H, FRAME, SHELVES, SHELF_H, STANDS_AT, buildLevel } from './level.js';
import { SCENE, bookPalette } from './palette.js';
import { plateLabel, sizedSprite } from './print.jsx';
import { useSeason } from './seasons/index.js';
import { FLOOR_TILE, MARKER, READER, WALL_PROPS, WALL_TILE, spriteStyle } from './sprites.js';

// The eight-bit level: the same shelves the classic theme draws, laid out as a
// side-scrolling room you travel along.
//
// What makes it a mode rather than a skin is that nobody is holding a
// controller. A television in a living room gets no input for months, so the
// level has to play itself: the camera patrols, the reader walks, and every so
// often he stops at whatever book he is standing in front of, takes it down,
// and the screen turns into that book's page for a few seconds. Anybody who
// does lean on the screen or pick up a remote takes over, and it goes back to
// patrolling when they stop.

// How the world is scaled to the screen. The world is always a whole number of
// pixels in both directions, so the art lands on the pixel grid at any size
// instead of a fixed 320x180 letterboxed into whatever the screen really is.
//
// The height sets the scale and the width follows - except that the width has
// a floor. These displays are mounted portrait as often as landscape, and at
// 720x1280 the height alone chose a scale of seven, which left a world 103
// pixels across: narrower than the status bar's own text. So the scale is
// whichever of the two dimensions asks for less.
// Tall enough for a five-shelf bookcase, its sign and its badge to stand on a
// floor under a status bar and still leave the books their ragged tops. At 180
// - which is what this was when a bookcase sized itself to its contents - five
// shelves left 19 pixels of clearance each, and every book on the level came
// out exactly the same height.
const NOMINAL_H = 216;
const MIN_WORLD_W = 220; // narrower than this and the status bar has no room
const MIN_SCALE = 2;
const MAX_SCALE = 12;

const WALK = 15; // world pixels a second the reader patrols at
const RUN = 260; // and catches up at, after somebody has dragged the view along
// And what the view travels at when a button or a key sends it somewhere.
//
// One fixed speed, which is the whole point of it. A press used to put the
// camera at its destination in the frame it arrived in, and the reader then
// ran after it at a speed that went with how far behind he was - so stepping
// from the end of one shelf to the start of the next was a cut, and a cut
// across a room reads as a fault rather than as a journey. Now the two of them
// travel together at this, and a step across six bookcases takes six times as
// long as a step across one, which is what a room being crossed looks like.
//
// A drag is not this and must not become it: a finger moves the view under it
// one to one, and there is nothing to travel to. See dragTo.
const PAN = 340;
const CLIMB = 42; // up a ladder
const DESCEND = 70; // and back down, which nobody wants to watch
const STEP_MS = 170; // how long one frame of the walk cycle holds, at WALK
const MIN_STEP_MS = 55; // and the fastest it ever cycles, however fast he is
const CLIMB_STEP_MS = 200;
const MANUAL_MS = 9000; // how long a person's input suspends the patrol
const NUDGE = 28; // world pixels an arrow key moves the view

// Where a shelf's first bookcase sits when the bar's navigation has just
// jumped to it: a little inside the left edge rather than flush against it, so
// it reads as the start of something rather than as a bookcase cut off by the
// frame.
const NAV_LEAD = 20;
// And how far past a shelf's start still counts as being at its start, which
// is what decides whether `<` goes back to where this shelf begins or on to
// the one before it. One bookcase: standing in front of the first case of Read
// and pressing back means To Read, because the start of Read is already on the
// screen.
const NAV_AT_START = CASE_W;
// What the bar needs to carry all four buttons.
//
// The bar is a grid of three tracks and the outer two are equal, so what it
// has to fit is twice the wider of its two ends, plus the buttons, plus its
// own padding. The count is the fixed one: "BOOKS 000" is nine characters of
// an eight-pixel face, seventy-two. The shelf end is a seven-pixel marker, a
// four-pixel gap and a plate of up to nine characters. Four buttons and their
// gaps are sixty-three, the padding is sixteen.
//
// For the four built-in shelves - none longer than READING - that comes to
// 223, and the sixteen pixels of headroom up to this number are what an
// eight-character shelf of somebody's own costs. A 720x1280 portrait display
// comes out at 240 and keeps all four; a phone at 400 wide comes out at 200
// and gets the two that matter, because what runs out on a narrow bar is not
// empty space, it is the shelf name.
const NAV_FULL_W = 240;
const EDGE = 26; // how close to the ends of the room the reader walks

// How far above his own feet the reader can get a book down. Everything higher
// than this is what the ladder is for: with a bookcase of five shelves it puts
// the bottom two in reach and the top three out of it.
const REACH = 46;
const MIN_CLIMB = 10; // below this he stretches rather than fetching a ladder
const HAND_AT = 8; // how far to the left of a book he stands to take it
const LADDER_W = 14;
const LADDER_OVER = 34; // how far a ladder rises above the rung he stands on

// The camera is eased rather than pinned to the reader, so that handing it back
// after a swipe - when he may still be running to catch up - is a glide and not
// a jump. Small enough that the patrol looks rigid and large enough that the
// hand-back does not.
const CAMERA_EASE_MS = 120;

const AMBIENT_GAP_MS = 16000; // between one book fetching itself and the next
const AMBIENT_HOLD_MS = 8500; // how long he holds it
const PICKED_HOLD_MS = 15000; // longer, when somebody chose it themselves

// Where the reader stands in the view: left of centre, because they are
// walking into the level rather than across it.
const READER_AT = 0.42;

const HUD_H = 22;

// Taken off the art rather than written down twice, since the reader is
// positioned by his centre and the sprite is what says where that is.
const READER_W = READER.idle.width;

export function viewportFor(width, height) {
  const fits = Math.min(Math.floor(height / NOMINAL_H), Math.floor(width / MIN_WORLD_W));
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, fits));
  // The screen's own width comes back too, and only the sheet reads it. The
  // room is measured in world pixels and does not care how big one is; the
  // sheet's lettering is in screen pixels, and the scale bottoms out at 2, so
  // a phone and a portrait television were indistinguishable to it. See
  // typeFor in BookSheet.jsx.
  return { scale, width, height, worldW: Math.ceil(width / scale), worldH: Math.ceil(height / scale) };
}

// Where the wall furniture hangs, measured down from the top of the screen,
// and how much of it there is.
//
// This is the one part of the level that has to know how tall the room turned
// out to be. Everything else stands on the floor, so on a portrait display -
// which is how half of these are mounted - the whole room sat in the bottom
// third under a storey of blank brick. A tall wall gets more rows of pictures
// on it, which is what a tall wall in a real room has.
const ABOVE_FURNITURE = CASE_H + 26; // a bookcase plus its sign and badge
const BAND_GAP = 44;
const BAND_SPACING = 168; // along the wall, between one picture and the next
const MAX_BANDS = 6;
const TALLEST_WALL_PROP = 26; // the window; nothing may hang where it would not fit

function wallBands(worldH) {
  const from = HUD_H + 6; // clear of the status bar
  // Down to where a full-height bookcase starts. Lower than that is not wrong
  // - the wall is behind the furniture and a picture half hidden by a bookcase
  // looks like a room - but it is wasted.
  const to = worldH - FLOOR_H - ABOVE_FURNITURE - 6;

  // Empty is a real answer, and the usual one in landscape. A five-shelf
  // bookcase with its sign and badge leaves about seven pixels of wall in a
  // 216-tall room, and a picture hung in them lands on top of the signs - which
  // is what happened. A tall room has space and gets rows of them; a short one
  // has bare brick, which is fine, because a room this full of furniture has
  // almost no wall showing anyway.
  const bands = [];
  for (let at = from; at + TALLEST_WALL_PROP <= to && bands.length < MAX_BANDS; at += BAND_GAP) {
    bands.push(at);
  }
  return bands;
}

// Which zone the camera is looking at. Clamped rather than allowed to come
// back empty, so the status bar always names something: the margins at each
// end of the room are outside every zone.
function zoneAt(zones, x) {
  if (!zones.length) return null;
  if (x < zones[0].from) return zones[0];
  for (const zone of zones) {
    if (x < zone.to) return zone;
  }
  return zones[zones.length - 1];
}

// One book is one element. The two ink bands across the spine are background
// layers rather than child spans, which is not a micro-optimisation: a shelf
// of four hundred books is the difference between twelve hundred nodes and
// four hundred, on hardware that is usually a television's.
function bookBackground({ face, light, dark, ink }) {
  return {
    background: `linear-gradient(${ink}, ${ink}) 0 4px/100% 1px no-repeat,`
      + ` linear-gradient(${ink}, ${ink}) 0 calc(100% - 5px)/100% 1px no-repeat,`
      + ` ${face}`,
    boxShadow: `inset 1px 0 0 ${light}, inset -1px 0 0 ${dark}, inset 0 1px 0 ${light}`,
  };
}

// The gap under a shelf's sign, which is where the range badge goes when a
// shelf takes more than one bookcase. The space is left whether or not there
// is a badge in it, so every sign on the level hangs at one height.
const BADGE_AT = CASE_H + 2;
const SIGN_AT = CASE_H + 14;

function Bookcase({ entry, onSelect }) {
  return (
    <div className="eb-case" style={{ left: `${entry.x}px`, width: `${CASE_W}px`, height: `${CASE_H}px` }}>
      <span className="eb-plate" style={{ bottom: `${SIGN_AT}px` }}>{plateLabel(entry.shelfKey)}</span>
      {entry.range ? (
        <span className="eb-badge" style={{ bottom: `${BADGE_AT}px` }}>{entry.range}</span>
      ) : null}
      <div className="eb-case-inner">
        {/* Every shelf is drawn, including the ones with nothing on them. A
            bookcase is a piece of furniture and it has five shelves whether or
            not the books have reached them yet. */}
        {Array.from({ length: SHELVES }, (_, r) => (
          <span
            key={`board-${r}`}
            className="eb-board"
            style={{ bottom: `${r * (SHELF_H + BOARD) - BOARD}px` }}
          />
        ))}
        {entry.rows.map((row) => {
          // Row positions come out of the level measured from the bookcase's
          // base; inside the frame everything is that much closer to it.
          const sits = row.bottom - FRAME;
          return row.books.map((slot) => (
            <button
              key={slot.book.id}
              type="button"
              className="eb-book"
              // Where the sheet grows from and shrinks back to. An id rather
              // than a position: the room is rebuilt whenever the shelves
              // change, and a book that has moved has moved.
              data-book={slot.book.id}
              title={slot.book.title}
              aria-label={slot.book.title}
              onClick={() => onSelect(slot.book)}
              style={{
                left: `${slot.left}px`,
                bottom: `${sits}px`,
                width: `${slot.w}px`,
                height: `${slot.h}px`,
                ...bookBackground(bookPalette(slot.book)),
              }}
            />
          ));
        })}
      </div>
    </div>
  );
}

// `expected` is how many books each shelf will have when every page of them
// has arrived, or null once they all have - see the preload in App.jsx. While
// it is set the room is laid out for the finished shelves and fills in as the
// books come, which is also how it is drawn before any have come at all.
export default function Level({ books, settings, active, expected = null }) {
  const shelves = useMemo(() => groupByShelf(books, settings, expected), [books, settings, expected]);
  const level = useMemo(() => buildLevel(shelves, settings, expected), [shelves, settings, expected]);
  // Whichever season the calendar is in, if any - see seasons/index.js. It
  // dresses the room and recasts the reader, and changes nothing about how
  // either behaves.
  const season = useSeason();

  const rootRef = useRef(null);
  const wallRef = useRef(null);
  const worldRef = useRef(null);
  const readerRef = useRef(null);

  const [view, setView] = useState({ scale: 4, width: 0, height: 0, worldW: 384, worldH: 216 });
  const [zone, setZone] = useState(null);
  const [pose, setPose] = useState('idle');
  // The book he has in his hands, as the level placed it - so the shelf can be
  // drawn with a gap in it where the book was.
  const [held, setHeld] = useState(null);
  const [ladder, setLadder] = useState(null);
  // What the full-screen sheet is showing, and how it got there.
  //
  // Separate from `held` on purpose: the sheet stays up while he walks to the
  // next book somebody turned to, where `held` is empty for those few seconds,
  // and a sheet that blinked out and back on every page turn would be
  // unusable.
  //
  //   entry    the book, as the level placed it
  //   auto     the level chose this one, so its page paces and scrolls itself
  //   enter    'grow' off the shelf, or 'next'/'prev' for a page turn
  //   from     that book's rectangle on screen, for growing out of and
  //            shrinking back into. Null for a page turn, which comes from the
  //            side of the screen rather than from the room.
  //   closing  the sheet is on its way out and must stay mounted to be seen
  //            going. Cleared by whatever picks the next book, so a book
  //            chosen during an exit simply replaces it.
  //
  // One object rather than four states because they change together, and a
  // half-applied set of them is a sheet animating the wrong way.
  const [picked, setPicked] = useState(null);

  // The reader is a small state machine and it lives in a ref, not in state.
  //
  // He has a position in the room rather than a seat on the screen, which is
  // what lets him walk to a book, climb to it, and be left behind by a swipe
  // and catch up. None of that is anything React needs to know frame by frame:
  // sixty renders a second of four hundred bookcases is the one thing a screen
  // this size cannot afford.
  //
  //   patrol -> goto -> climb -> read -> down -> patrol
  //
  // Every arrow is taken in the animation frame below; the only entry points
  // from outside are fetch() and the input handlers.
  const motion = useRef({
    phase: 'patrol',
    readerX: 0,
    facing: 1,
    camera: 0,
    // Where the view is travelling to, or null when it is not travelling. Set
    // by a button or a key; a drag clears it, because a finger is already
    // where it meant to be. See PAN.
    panTo: null,
    targetX: 0,
    // How fast he is going, in world pixels a second, whatever is moving him.
    // Only the walk cycle reads it: legs that take the same time to cross a
    // pixel and to cross a room are the most obvious way a sprite looks wrong.
    speed: 0,
    climbY: 0,
    climbTo: 0,
    fetching: null,
    picked: false,
    after: null, // what to do once he is back down the ladder
    stepAt: 0,
    holdUntil: 0,
    nextAmbient: 0,
    manualUntil: 0,
    // Whether a person asked for the book he is fetching or the level chose it
    // on its own. Named for who asked rather than after the `picked` state
    // above, which is a different thing - that one is which book the sheet is
    // showing, and this is the only thing that decides whether the sheet on it
    // has a timer.
    byHand: false,
    // Whether the full-screen sheet is up. On the ref rather than read off
    // `picked` because the animation frame is the only reader and it must not
    // be re-created sixty times a second to learn it.
    sheetOpen: false,
  });

  const maxCamera = Math.max(0, level.width - view.worldW);
  const minX = Math.min(EDGE, level.width / 2);
  const maxX = Math.max(minX, level.width - EDGE);

  // A level narrower than the screen is centred rather than pinned to the
  // left, which is the difference between a two-book shelf looking deliberate
  // and looking like a rendering fault.
  const originX = Math.max(0, Math.round((view.worldW - level.width) / 2));

  // Where a book is on the screen right now, in the root's own co-ordinates -
  // which are the sheet's, because the two cover the same box.
  //
  // Read off the element rather than worked out from the world position and
  // the camera. The arithmetic is doable and would have to know what the
  // stage's transform-origin is, what the world's translate is and which way
  // each of them rounds; the element already knows, and is asked twice a
  // minute at the very most.
  const bookRect = useCallback((entry) => {
    const root = rootRef.current;
    if (!root || !entry) return null;
    let el = null;
    try {
      // Ids are Goodreads URLs - query strings, colons, hashes and commas in
      // them - so they are not selector-safe as they stand.
      el = root.querySelector(`[data-book="${CSS.escape(entry.book.id)}"]`);
    } catch {
      return null;
    }
    if (!el) return null;

    const box = el.getBoundingClientRect();
    const base = root.getBoundingClientRect();
    // The frame comes with it: the sheet has to scale itself down to this
    // rectangle, and what it is a fraction OF is the box both of them fill.
    return {
      x: box.left - base.left,
      y: box.top - base.top,
      w: box.width,
      h: box.height,
      frameW: base.width,
      frameH: base.height,
    };
  }, []);

  // Send him to fetch a book: walk to it, and climb to it if it is above his
  // reach. Called both by a person clicking a spine and by the level deciding
  // on its own that it is time something was taken off a shelf.
  const fetch = useCallback((entry, byHand) => {
    if (!entry) return;
    const m = motion.current;
    m.fetching = entry;
    m.byHand = byHand;
    // Wherever the view was going, it was going there instead of here.
    m.panTo = null;
    m.targetX = Math.max(minX, Math.min(maxX, entry.worldX - HAND_AT));

    // The bookcase stands on the room's floor, so a shelf's height above that
    // is its height above his feet, less where he stands.
    const worldY = FLOOR_H - 1 + entry.shelfY;
    const needed = Math.max(0, worldY - (STANDS_AT + REACH));
    m.climbTo = needed < MIN_CLIMB ? 0 : needed;

    setHeld(null);
    // Anything he is holding goes back and he comes down first. Vanishing off
    // a ladder mid-air is the one thing here that would look like a bug.
    if (m.climbY > 0) {
      m.after = 'goto';
      m.phase = 'down';
    } else {
      m.phase = 'goto';
    }
  }, [minX, maxX]);

  // Somebody has tapped a spine. This sends him for it and nothing else: the
  // sheet opens when he has the book in his hands, exactly where the level's
  // own does.
  //
  // Popping it straight away was the first version and it was the wrong one.
  // The whole of this theme is a reader who walks to a shelf and goes up a
  // ladder for the top three, and a sheet that appeared on the tap covered all
  // of it - the one moment somebody is actually watching the screen is the one
  // moment the screen stopped showing the room. The tap is answered by him
  // turning and running, which is the acknowledgement.
  const pick = useCallback((book) => {
    fetch(level.placed.find((entry) => entry.book.id === book.id), true);
  }, [fetch, level]);

  // Where the open book stands in the room: its place in `placed`, which is
  // what turning a page steps along. -1 when there is no book open, or when the
  // one that is open is not in this room any more.
  //
  // It used to work out a position on the shelf as well, for a counter in the
  // sheet's bar, and that counter is gone - so the run-scanning that produced
  // it went with it rather than being left to compute something nobody prints.
  const where = useMemo(() => {
    if (!picked) return -1;
    return level.placed.findIndex((item) => item.book.id === picked.entry.book.id);
  }, [level, picked]);

  // Turn to the book beside this one, in the order the shelf was drawn in.
  //
  // Along the whole room rather than around one shelf: the last book of To
  // Read stands next to the first book of Read with a houseplant between them,
  // and stopping dead there would be a wall that is not in the room. The two
  // ends of the room are, so it clamps there.
  // Unlike a pick, this changes the sheet immediately and sends him after it,
  // rather than waiting for him to arrive. Somebody turning pages is reading,
  // not watching the room, and dropping them back into it between every two
  // books would make the shelf unbrowsable. He catches up behind the sheet,
  // and by the time it is closed again he is at the right shelf.
  const step = useCallback((by) => {
    const placed = level.placed;
    if (where < 0 || !placed.length) return;
    const to = Math.max(0, Math.min(placed.length - 1, where + by));
    if (to === where) return;
    // In from whichever side it was turned towards, rather than out of the
    // shelf: a page turn is a page turn, and the book it is going to is one he
    // has not walked to yet.
    setPicked({ entry: placed[to], auto: false, enter: by > 0 ? 'next' : 'prev', from: null, closing: false });
    fetch(placed[to], true);
  }, [level, where, fetch]);

  // Asks the sheet to leave. It stays mounted and shrinking for as long as its
  // own exit runs, and `sheetGone` below is what actually takes it away - so
  // the one thing this must do is work out where it is shrinking back to,
  // which is wherever that book is standing at this moment.
  const dismiss = useCallback(() => {
    setPicked((current) => {
      if (!current || current.closing) return current;
      return { ...current, closing: true, from: bookRect(current.entry) || current.from };
    });
  }, [bookRect]);

  // How long the page it opened on its own needs, which only the page knows:
  // how much of it there is below the fold. The base is AMBIENT_HOLD_MS and
  // this only ever extends it - see BookSheet.jsx.
  //
  // Ignored while somebody is in charge. `byHand` means a person asked for this
  // book, and a reading somebody asked for has no timer at all.
  const readFor = useCallback((ms) => {
    const m = motion.current;
    if (m.byHand || m.phase !== 'read') return;
    m.holdUntil = Math.max(m.holdUntil, performance.now() + ms);
  }, []);

  const sheetGone = useCallback(() => {
    setPicked((current) => (current && current.closing ? null : current));
  }, []);

  const closeSheet = useCallback(() => {
    const m = motion.current;
    m.sheetOpen = false;
    dismiss();

    if (m.phase === 'read') {
      // He has been standing at the shelf with the book in his hands the whole
      // time the sheet was up. Closing it gives him the same while to be seen
      // holding it as he would have had if nobody had opened one, rather than
      // ending a read that was never watched.
      m.holdUntil = performance.now() + PICKED_HOLD_MS;
    } else {
      // Closed while he was still on his way to it, which is now a real window
      // - the sheet does not open until he arrives. The errand is off: left
      // alone he would walk the rest of the way, take the book down, and pop
      // open the sheet somebody had just shut.
      m.fetching = null;
      m.climbTo = 0;
    }
  }, [dismiss]);


  // Taking the view off its patrol, which is the whole of what a person can do
  // to this display. A drag, an arrow key and a button in the status bar all
  // arrive here first: whatever they meant, the reader's errand is off, the
  // book goes back, and a sheet that is up is sent back into the shelf.
  const takeOver = useCallback(() => {
    const m = motion.current;
    m.manualUntil = performance.now() + MANUAL_MS;
    m.fetching = null;
    m.climbTo = 0;
    m.sheetOpen = false;
    setHeld(null);
    // Sent back rather than taken away: somebody driving the view has asked
    // for the room, and the sheet going back where it came from is how the
    // room arrives.
    dismiss();
    // And if he is up a ladder he comes down it before going anywhere.
    if (m.climbY > 0) {
      m.after = 'goto';
      m.phase = 'down';
    } else {
      m.phase = 'goto';
    }
    return m;
  }, [dismiss]);

  // A button or a key: the view is sent somewhere and travels there, at PAN, with
  // the reader carried along at the same speed. Nothing moves in this call - it
  // sets the destination and the animation frame does the walking.
  const driveTo = useCallback((cameraX) => {
    takeOver().panTo = Math.max(0, Math.min(maxCamera, cameraX));
  }, [takeOver, maxCamera]);

  // A finger or a wheel: the view moves with it, one to one, because a drag
  // that travelled to where the finger had got to would lag under the hand.
  // The reader is left behind by it and runs after it, which is the one place
  // that behaviour belongs.
  const dragTo = useCallback((cameraX) => {
    const m = takeOver();
    m.panTo = null;
    m.camera = Math.max(0, Math.min(maxCamera, cameraX));
    m.targetX = Math.max(minX, Math.min(maxX, m.camera + view.worldW * READER_AT));
  }, [takeOver, maxCamera, minX, maxX, view.worldW]);

  // Where the camera stands to show a shelf from its beginning.
  //
  // Its first bookcase a little inside the left edge, and then pulled back far
  // enough that the middle of the screen lands inside the shelf rather than
  // past it. That second half is not framing, it is the bar agreeing with the
  // buttons: the status bar names whatever the camera's centre is over, and a
  // room 550 pixels wide on a screen 240 wide holds two whole shelves at once.
  // Put To Read at the left edge and the middle of the screen is over Read -
  // so pressing "next shelf" moved to To Read and the bar said READ, and the
  // press after that appeared to do nothing at all.
  //
  // On a shelf wider than half a screen - anything past about sixty books - it
  // is the left edge and this changes nothing.
  const cameraForShelf = useCallback(
    (zone) => Math.min(zone.from - NAV_LEAD, zone.to - view.worldW / 2 - 1),
    [view.worldW]
  );

  // The status bar's shelf navigation: one shelf back, or one shelf on.
  //
  // Back does not always mean the shelf before this one. A shelf can be six
  // bookcases long, and from the middle of Read the thing somebody wants first
  // is the start of Read - the same rule a music player uses, and for the same
  // reason: the section you are in is a destination too. Only from the start
  // of a shelf does back mean the previous shelf.
  //
  // Which shelf that is is read off the camera here rather than off the `zone`
  // state, because the two are the same thing and the ref is the one that is
  // never a frame behind - and off where the view is going when it is going
  // somewhere, because the journey takes real time now and pressing > twice
  // has to mean two shelves on rather than the same one again.
  const goToShelf = useCallback((by) => {
    const zones = level.zones;
    if (!zones.length) return;

    const m = motion.current;
    const at = m.panTo === null ? m.camera : m.panTo;
    const here = zoneAt(zones, at + view.worldW / 2);
    const index = Math.max(0, zones.findIndex((zone) => zone.shelfKey === here.shelfKey));

    if (by > 0) {
      // Off the end of the last shelf is the end of the room, which is where
      // the last books actually are - a shelf's start is not its end.
      if (index + 1 >= zones.length) driveTo(maxCamera);
      else driveTo(cameraForShelf(zones[index + 1]));
      return;
    }

    const start = cameraForShelf(zones[index]);
    if (at > start + NAV_AT_START) driveTo(start);
    else if (index > 0) driveTo(cameraForShelf(zones[index - 1]));
    else driveTo(0);
  }, [level, view.worldW, maxCamera, cameraForShelf, driveTo]);

  // A new level is a different room, and he was standing in the old one.
  useEffect(() => {
    const m = motion.current;
    m.phase = 'patrol';
    m.fetching = null;
    m.after = null;
    m.climbY = 0;
    m.climbTo = 0;
    m.readerX = Math.max(minX, Math.min(maxX, m.readerX || minX));
    m.camera = Math.max(0, Math.min(maxCamera, m.camera));
    // Wherever the view was going, it was going there in the old room.
    m.panTo = null;
    m.nextAmbient = performance.now() + AMBIENT_GAP_MS;
    m.sheetOpen = false;
    setHeld(null);
    setLadder(null);
    // The open book was a place in the old room. Even the same book is at a
    // different x now, so the sheet goes at once rather than shrinking back
    // into a shelf that has moved out from under it.
    setPicked(null);
  }, [level, minX, maxX, maxCamera]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const measure = () => setView(viewportFor(el.clientWidth, el.clientHeight));
    measure();

    // A ResizeObserver rather than a window listener: the admin site renders
    // this inside an iframe that its own layout resizes, and a window resize
    // is not what happens there.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return undefined;

    const m = motion.current;
    let raf = 0;
    let last = performance.now();

    function walkToward(target, dt) {
      const gap = target - m.readerX;
      if (Math.abs(gap) <= 1) {
        m.readerX = target;
        return true;
      }
      // Further than a couple of screens is not a walk anybody is watching -
      // he is off the side of the room - so he is simply already there.
      if (Math.abs(gap) > view.worldW * 2) {
        m.readerX = target;
        return true;
      }
      m.facing = gap > 0 ? 1 : -1;
      // Faster the further behind he is, which is what turns a swipe across
      // the room into a run rather than a very long stroll.
      const speed = Math.min(RUN, Math.max(WALK, Math.abs(gap) * 2.2));
      m.speed = speed;
      m.readerX += Math.sign(gap) * Math.min(Math.abs(gap), speed * (dt / 1000));
      return false;
    }

    function take(now) {
      m.phase = 'read';
      m.holdUntil = now + (m.byHand ? PICKED_HOLD_MS : AMBIENT_HOLD_MS);
      m.facing = 1; // towards the shelf he is taking it from
      setHeld(m.fetching);
      // And the sheet opens on it, whoever asked for it. This is the one place
      // it opens from: a book is on the screen when it is in his hands and not
      // before, so the walk and the ladder are never covered by it.
      //
      // For a page turn this is already the book on the screen and setting it
      // again costs nothing; for a pick and for the level's own choice it is
      // the moment the sheet appears.
      m.sheetOpen = true;
      // Out of the book itself, from where it is standing on the shelf.
      //
      // Left alone when it is already this book's sheet on the screen: that is
      // a page turn, where he has just caught up with a sheet that has been up
      // since the turn, and re-entering it here would play the animation over
      // a page somebody is already reading.
      const from = m.fetching;
      setPicked((current) => (
        current && !current.closing && current.entry.book.id === from.book.id
          ? current
          : { entry: from, auto: !m.byHand, enter: 'grow', from: bookRect(from), closing: false }
      ));
    }

    function idle(now) {
      m.phase = 'patrol';
      m.fetching = null;
      m.nextAmbient = now + AMBIENT_GAP_MS;
    }

    function draw(now) {
      raf = requestAnimationFrame(draw);
      // Capped: a tab that was hidden for an hour hands back an hour of
      // elapsed time, and the reader would cross the room in one frame.
      const dt = Math.min(100, now - last);
      last = now;

      // The view travelling to where a button sent it, at one speed, carrying
      // the reader with it - the two of them are one movement, which is the
      // whole of what this is for.
      //
      // Not while he is on a ladder: he comes down first. Pinning him to the
      // view while he is on a rung would slide him off it sideways, and the
      // ladder is drawn where it stands in the room.
      if (m.panTo !== null && m.phase !== 'climb' && m.phase !== 'down') {
        const gap = m.panTo - m.camera;
        const move = PAN * (dt / 1000);
        if (Math.abs(gap) <= move) {
          m.camera = m.panTo;
          m.panTo = null;
        } else {
          m.camera += Math.sign(gap) * move;
        }
        // He keeps his seat on the screen for the whole trip rather than
        // running after it: what was asked for was one movement at one speed,
        // and a sprite chasing the view is two.
        const seat = Math.max(minX, Math.min(maxX, m.camera + view.worldW * READER_AT));
        if (seat !== m.readerX) m.facing = seat > m.readerX ? 1 : -1;
        m.readerX = seat;
        m.targetX = seat;
        m.speed = PAN;
        // And the patrol stays off for the length of the journey, however long
        // that is - a room several hundred bookcases long outlasts MANUAL_MS.
        m.manualUntil = Math.max(m.manualUntil, now + MANUAL_MS);
      }

      const manual = m.manualUntil > now;

      if (m.phase === 'patrol') {
        m.speed = WALK;
        m.readerX += m.facing * WALK * (dt / 1000);
        if (m.readerX >= maxX) {
          m.readerX = maxX;
          m.facing = -1;
        } else if (m.readerX <= minX) {
          m.readerX = minX;
          m.facing = 1;
        }
        // Whatever he is nearest, which is what makes it look like he stopped
        // to pick something up rather than like a slideshow with a sprite in
        // it.
        if (!manual && !m.sheetOpen && now >= m.nextAmbient && level.placed.length) {
          let nearest = level.placed[0];
          for (const candidate of level.placed) {
            if (Math.abs(candidate.worldX - m.readerX) < Math.abs(nearest.worldX - m.readerX)) {
              nearest = candidate;
            }
          }
          fetch(nearest, false);
        }
      } else if (m.phase === 'goto') {
        // Carried rather than walking, while the view is travelling.
        if (m.panTo === null && walkToward(m.targetX, dt)) {
          if (m.fetching && m.climbTo > 0) {
            setLadder({
              x: Math.round(m.fetching.worldX - LADDER_W / 2),
              height: Math.round(m.climbTo + LADDER_OVER),
            });
            m.phase = 'climb';
          } else if (m.fetching) {
            take(now);
          } else {
            idle(now);
          }
        }
      } else if (m.phase === 'climb') {
        if (!m.fetching) {
          // The errand was called off on the way up - a sheet closed while he
          // was climbing to the book a page turn sent him for. There is
          // nothing to take, so he comes back down rather than "arriving" at
          // the zero closing it set climbTo to and taking nothing off the
          // shelf, which opened a sheet on no book and threw.
          m.after = null;
          m.phase = 'down';
        } else {
          m.climbY = Math.min(m.climbTo, m.climbY + CLIMB * (dt / 1000));
          if (m.climbY >= m.climbTo) take(now);
        }
      } else if (m.phase === 'read') {
        // Only a sheet somebody opened themselves suspends the timer. The
        // level's own reading has to end on its own, or a television would
        // show one book's page from now until somebody unplugged it; a person
        // reading a summary they asked for has to not have it taken away.
        if (!(m.byHand && m.sheetOpen) && now >= m.holdUntil) {
          m.sheetOpen = false;
          // Back into the shelf it came out of, the same way a person closing
          // it sends it.
          dismiss();
          setHeld(null);
          if (m.climbY > 0) {
            m.after = null;
            m.phase = 'down';
          } else {
            idle(now);
          }
        }
      } else if (m.phase === 'down') {
        m.climbY = Math.max(0, m.climbY - DESCEND * (dt / 1000));
        if (m.climbY <= 0) {
          setLadder(null);
          if (m.after === 'goto') {
            m.after = null;
            m.phase = 'goto';
          } else {
            idle(now);
          }
        }
      }

      // What he looks like doing it. The walk cycle runs only while he moves,
      // because legs going on the spot is the most obvious way a sprite looks
      // wrong.
      const onLadder = m.climbY > 0 || m.phase === 'climb';
      let want;
      if (m.phase === 'climb' || m.phase === 'down') {
        if (now - m.stepAt >= CLIMB_STEP_MS) m.stepAt = now;
        want = Math.floor(now / CLIMB_STEP_MS) % 2 ? 'climb1' : 'climb2';
      } else if (m.phase === 'read') {
        // On a ladder the reaching torso and the ladder legs are the same
        // frame, which is exactly the pose wanted.
        want = onLadder ? 'climb1' : 'reach';
      } else if (m.phase === 'goto' || m.phase === 'patrol') {
        // Off his speed rather than fixed. A cycle that took the same time at a
        // patrol and at a run made him stroll across the room with the view,
        // which is the thing the eye notices first about a sprite.
        const cycle = Math.max(MIN_STEP_MS, STEP_MS * (WALK / Math.max(WALK, m.speed)));
        want = Math.floor(now / cycle) % 2 ? 'step1' : 'step2';
      } else {
        want = 'idle';
      }
      setPose((current) => (current === want ? current : want));

      // The camera follows him, except while somebody is driving it - and then
      // it is eased back rather than snapped, because he may still be running
      // to where it went.
      const wanted = manual
        ? m.camera
        : Math.max(0, Math.min(maxCamera, m.readerX - view.worldW * READER_AT));
      m.camera += (wanted - m.camera) * Math.min(1, dt / CAMERA_EASE_MS);

      const camera = Math.round(m.camera);
      if (wallRef.current) {
        // Half speed: the bookcases are in the room and the wall is behind it.
        wallRef.current.style.transform = `translate3d(${-Math.round(camera * 0.5)}px, 0, 0)`;
      }
      if (worldRef.current) {
        worldRef.current.style.transform = `translate3d(${originX - camera}px, 0, 0)`;
      }
      if (readerRef.current) {
        const at = Math.round(originX + m.readerX - camera - READER_W / 2);
        readerRef.current.style.transform =
          `translate3d(${at}px, ${-Math.round(m.climbY)}px, 0) scaleX(${m.facing < 0 ? -1 : 1})`;
      }

      const here = zoneAt(level.zones, m.camera + view.worldW / 2);
      if (here) setZone((current) => (current?.shelfKey === here.shelfKey ? current : here));
    }

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [active, maxCamera, minX, maxX, originX, level, view.worldW, fetch, bookRect, dismiss]);

  // Anybody who does touch the screen takes the view off its patrol for a
  // while and sends it down the room - and the reader comes after it. Three
  // ways in, because a display might be a television with a remote, a tablet
  // on a counter, or a laptop previewing it.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    // A wheel and a finger move the view under them; a key sends it somewhere
    // and it travels. A nudge is measured from wherever the view is going
    // rather than from where it has got to, so holding an arrow down adds up
    // instead of fighting the journey it started.
    const drag = (delta) => {
      if (!delta) return;
      dragTo(motion.current.camera + delta);
    };
    const nudge = (delta) => {
      const m = motion.current;
      driveTo((m.panTo === null ? m.camera : m.panTo) + delta);
    };

    function onWheel(event) {
      // Both axes: a trackpad swipe gives deltaX, a mouse wheel gives only
      // deltaY, and in a room that runs sideways both of them mean "along".
      drag(event.deltaX || event.deltaY);
      event.preventDefault();
    }

    // The arrows move the view along; the keys either side of them do what the
    // status bar's buttons do, because a room several hundred bookcases long
    // is not one an arrow key crosses. A television remote sends arrows and a
    // keyboard has the rest.
    function onKeyDown(event) {
      if (event.key === 'ArrowRight') nudge(NUDGE);
      else if (event.key === 'ArrowLeft') nudge(-NUDGE);
      else if (event.key === 'PageDown') goToShelf(1);
      else if (event.key === 'PageUp') goToShelf(-1);
      else if (event.key === 'End') driveTo(maxCamera);
      else if (event.key === 'Home') driveTo(0);
      else return;
      event.preventDefault();
    }

    let touchAt = null;
    const onTouchStart = (event) => { touchAt = event.touches[0]?.clientX ?? null; };
    function onTouchMove(event) {
      if (touchAt === null) return;
      const x = event.touches[0]?.clientX ?? touchAt;
      // Divided by the scale, because the finger moved screen pixels and the
      // room is measured in world ones.
      drag((touchAt - x) / view.scale);
      touchAt = x;
      event.preventDefault();
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [driveTo, dragTo, goToShelf, maxCamera, view.scale]);

  // The room is held across renders deliberately. The walk cycle, the status
  // bar and the open sheet all set state several times a second, and without
  // this every one of them would rebuild every bookcase in the level.
  const room = useMemo(() => (
    <>
      {level.props.map((prop, i) => {
        // A season may draw a prop as something else. It stands where the
        // prop would have, centred on the same spot if it is another width.
        const sprite = season?.props?.[prop.name] ?? prop.sprite;
        const x = prop.x + Math.round((prop.sprite.width - sprite.width) / 2);
        return (
          <i key={`${prop.name}-${i}`} data-prop={prop.name} style={spriteStyle(sprite, { x, bottom: 0 })} />
        );
      })}
      {level.cases.map((entry) => (
        <Bookcase key={`${entry.shelfKey}-${entry.caseIndex}`} entry={entry} onSelect={pick} />
      ))}
    </>
  ), [level, pick, season]);

  // Wall furniture is a function of the room's dimensions rather than of the
  // shelves, so it is placed here and not in buildLevel: how many rows of it
  // there is room for is not known until the screen has been measured. Each
  // band is offset along the wall from the one below, so they do not line up
  // into columns.
  const wall = useMemo(() => {
    const bands = wallBands(view.worldH);
    const out = [];
    bands.forEach((top, band) => {
      for (let x = 40 + band * 63, i = 0; x < level.width - 30; x += BAND_SPACING, i++) {
        const prop = WALL_PROPS[(band + i) % WALL_PROPS.length];
        out.push(
          <i
            key={`${band}-${x}`}
            className="eb-wall-prop"
            style={{ left: `${x}px`, top: `${top}px`, ...sizedSprite(prop.sprite) }}
          />
        );
      }
    });
    return out;
  }, [level.width, view.worldH]);

  // What the season adds, held across renders for the reason `room` is: the
  // walk cycle sets state several times a second, and a season's decoration
  // is a function of the room and the screen and of nothing that changes that
  // often. Its own movement is all CSS.
  const seasonRoom = useMemo(
    () => (season?.Room ? <season.Room level={level} worldH={view.worldH} ceiling={HUD_H} /> : null),
    [season, level, view.worldH]
  );
  const seasonScreen = useMemo(
    () => (season?.Screen ? <season.Screen worldW={view.worldW} worldH={view.worldH} ceiling={HUD_H} /> : null),
    [season, view.worldW, view.worldH]
  );

  const cast = season?.reader || READER;
  const reader = cast[pose] || cast.idle;

  // The sheet is a sibling of the room rather than a child of it, which is
  // three things at once: it is not inside the stage, so its lettering is in
  // screen pixels at a size somebody can read a paragraph at instead of in
  // world pixels; it is outside .eb, whose touch-action: none would have
  // forbidden scrolling the summary; and its wheel, touch and click events
  // never reach the handlers that drive the camera, so no page turn also
  // sends the room sideways.
  return (
    <>
      <div className="eb" ref={rootRef} data-season={season?.id} style={{ background: SCENE.wallDark }}>
        <div
          className="eb-stage"
          style={{
            width: `${view.worldW}px`,
            height: `${view.worldH}px`,
            transform: `scale(${view.scale})`,
          }}
        >
          <div
            className="eb-wall"
            ref={wallRef}
            style={{
              // Wide enough to still cover the screen at the far end of the
              // room, which at half speed is half the room plus a screenful.
              width: `${Math.ceil(level.width / 2) + view.worldW * 2}px`,
              bottom: `${FLOOR_H}px`,
              backgroundImage: WALL_TILE.url,
              backgroundSize: `${WALL_TILE.width}px ${WALL_TILE.height}px`,
            }}
          >
            {wall}
          </div>

          <div className="eb-world" ref={worldRef} style={{ width: `${level.width}px` }}>
            <div
              className="eb-floor"
              style={{
                height: `${FLOOR_H}px`,
                backgroundImage: FLOOR_TILE.url,
                backgroundSize: `${FLOOR_TILE.width}px ${FLOOR_TILE.height}px`,
              }}
            />
            <div className="eb-room" style={{ bottom: `${FLOOR_H - 1}px` }}>
              {room}
              {/* The gap the book leaves while he has it. Drawn over the shelf
                  rather than by hiding the book itself, which would mean
                  rebuilding every bookcase in the level to take one book out. */}
              {held ? (
                <span
                  className="eb-gap"
                  style={{
                    left: `${held.left}px`,
                    bottom: `${held.shelfY}px`,
                    width: `${held.w}px`,
                    height: `${held.h}px`,
                  }}
                />
              ) : null}
              {ladder ? (
                <div
                  className="eb-ladder"
                  style={{ left: `${ladder.x}px`, width: `${LADDER_W}px`, height: `${ladder.height}px` }}
                />
              ) : null}
            </div>
            {seasonRoom ? <div className="eb-season">{seasonRoom}</div> : null}
          </div>

          <i
            className="eb-reader"
            ref={readerRef}
            style={{ left: 0, bottom: `${STANDS_AT}px`, ...sizedSprite(reader) }}
          />

          {seasonScreen ? <div className="eb-season">{seasonScreen}</div> : null}

          <div className="eb-hud" style={{ height: `${HUD_H}px` }}>
            <span className="eb-hud-zone">
              <i className="eb-marker" style={sizedSprite(MARKER)} />
              <span key={zone?.shelfKey}>{plateLabel(zone?.shelfKey)}</span>
            </span>
            {/* Somewhere to go, for the one person a year who is standing in
                front of this with a phone in their hand. A swipe crosses a
                room a screen at a time, which is fine for a shelf and hopeless
                for four hundred bookcases, and the shelf the level names in
                this bar is exactly the unit somebody wants to move by.

                Drawn in the bar rather than floated over the room because
                everything in this theme stands somewhere, and a control that
                faded in over the bookcases would be the one thing here that
                behaves like a web page. */}
            {/* The symbol is in a span of its own so the button can centre it
                as a block: a bitmap glyph hangs off a baseline like any other,
                and centring the line box it sits in is not the same thing as
                centring the ink somebody is looking at. */}
            <span className="eb-hud-nav">
              {view.worldW >= NAV_FULL_W ? (
                <button type="button" className="eb-nav" aria-label="First shelf"
                  onClick={() => driveTo(0)}><span>{'<<'}</span></button>
              ) : null}
              <button type="button" className="eb-nav" aria-label="Previous shelf"
                onClick={() => goToShelf(-1)}><span>{'<'}</span></button>
              <button type="button" className="eb-nav" aria-label="Next shelf"
                onClick={() => goToShelf(1)}><span>{'>'}</span></button>
              {view.worldW >= NAV_FULL_W ? (
                <button type="button" className="eb-nav" aria-label="Last shelf"
                  onClick={() => driveTo(maxCamera)}><span>{'>>'}</span></button>
              ) : null}
            </span>
            <span className="eb-hud-count">
              {`BOOKS ${String(zone?.count ?? 0).padStart(3, '0')}`}
            </span>
          </div>
        </div>
      </div>

      {picked ? (
        // Keyed on the book, so turning the page remounts the sheet: the summary
        // scrolls back to the top and the entrance runs again, which is what a
        // page turn looks like. Keeping it and swapping the text would leave
        // the reader halfway down the previous book's summary.
        <BookSheet
          key={picked.entry.book.id}
          book={picked.entry.book}
          shelfKey={picked.entry.shelfKey}
          scale={view.scale}
          width={view.width}
          auto={Boolean(picked.auto)}
          hold={AMBIENT_HOLD_MS}
          enter={picked.enter}
          from={picked.from}
          closing={picked.closing}
          onClose={closeSheet}
          onClosed={sheetGone}
          onReading={readFor}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
        />
      ) : null}
    </>
  );
}
